import { useEffect, useState } from 'react'
import api from '../services/api'

interface Parcela {
  num: number
  dias: number
  percentual: number
}

interface CondicaoItem {
  tcv_cod: number
  tcv_des: string
  tcv_tip: string
}

interface CondicaoDetalhe {
  tcv_cod: number
  tcv_des: string
  tcv_com: string
  tcv_tip: string
  tcv_gerliq: string
  tcv_pulemi: string
  tcv_finsem: string
  tcv_fatcon: string
  tcv_obs: string
  parcelas: Parcela[]
}

const OPCOES_TIPO = [
  { value: 'P', label: 'A Prazo' },
  { value: 'A', label: 'A Vista' },
]

const OPCOES_VENCIMENTO_A_PARTIR = [
  { value: 'F', label: 'Da Fatura' },
  { value: 'C', label: 'Do Conhecimento' },
  { value: 'U', label: 'Do Último Conhecimento' },
  { value: 'I', label: 'De Cada Conhecimento' },
  { value: 'V', label: 'Informa Vencimento' },
]

const OPCOES_VENCIMENTO_FDS = [
  { value: 'F', label: 'Fica' },
  { value: 'A', label: 'Antecipa' },
  { value: 'P', label: 'Posterga' },
]

const NOVA_CONDICAO: CondicaoDetalhe = {
  tcv_cod: 0,
  tcv_des: '',
  tcv_com: '',
  tcv_tip: 'P',
  tcv_gerliq: 'S',
  tcv_pulemi: 'N',
  tcv_finsem: 'F',
  tcv_fatcon: 'F',
  tcv_obs: '',
  parcelas: [{ num: 1, dias: 30, percentual: 100 }],
}

function Campo({ label, width, children }: { label: string; width?: number | string; children: React.ReactNode }) {
  return (
    <div className="field-cad" style={{ width }}>
      <label style={{ display: 'block', minHeight: '1.1rem', lineHeight: 1.2 }}>{label}</label>
      {children}
    </div>
  )
}

function fmtPercentual(v: number) {
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
}

export default function CondicaoPagamento() {
  const [lista, setLista] = useState<CondicaoItem[]>([])
  const [busca, setBusca] = useState('')
  const [carregandoLista, setCarregandoLista] = useState(false)
  const [selecionado, setSelecionado] = useState<CondicaoDetalhe | null>(null)
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false)
  const [form, setForm] = useState<CondicaoDetalhe | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [msgSalvar, setMsgSalvar] = useState<{ texto: string; ok: boolean } | null>(null)

  async function carregarLista(termo: string) {
    setCarregandoLista(true)
    try {
      const r = await api.get('/condicao-pagamento', { params: termo ? { busca: termo } : {} })
      setLista(r.data)
    } finally {
      setCarregandoLista(false)
    }
  }

  useEffect(() => { carregarLista('') }, [])
  useEffect(() => {
    const t = setTimeout(() => carregarLista(busca), 350)
    return () => clearTimeout(t)
  }, [busca])

  async function selecionar(cod: number) {
    setCarregandoDetalhe(true)
    setMsgSalvar(null)
    try {
      const r = await api.get(`/condicao-pagamento/${cod}`)
      setSelecionado(r.data)
      setForm({ ...r.data, parcelas: r.data.parcelas.map((p: Parcela) => ({ ...p })) })
    } finally {
      setCarregandoDetalhe(false)
    }
  }

  function nova() {
    setSelecionado(NOVA_CONDICAO)
    setForm({ ...NOVA_CONDICAO, parcelas: NOVA_CONDICAO.parcelas.map(p => ({ ...p })) })
    setMsgSalvar(null)
  }

  function setCampo<K extends keyof CondicaoDetalhe>(campo: K, valor: CondicaoDetalhe[K]) {
    setForm(f => f && { ...f, [campo]: valor })
  }

  function setParcela(idx: number, campo: keyof Parcela, valor: number) {
    setForm(f => {
      if (!f) return f
      const parcelas = f.parcelas.map((p, i) => (i === idx ? { ...p, [campo]: valor } : p))
      return { ...f, parcelas }
    })
  }

  function adicionarParcela() {
    setForm(f => {
      if (!f) return f
      return { ...f, parcelas: [...f.parcelas, { num: f.parcelas.length + 1, dias: 0, percentual: 0 }] }
    })
  }

  function removerParcela(idx: number) {
    setForm(f => {
      if (!f) return f
      const parcelas = f.parcelas.filter((_, i) => i !== idx).map((p, i) => ({ ...p, num: i + 1 }))
      return { ...f, parcelas }
    })
  }

  const totalParcelas = form ? form.parcelas.reduce((s, p) => s + p.percentual, 0) : 0
  const totalDivergente = form ? Math.abs(totalParcelas - 100) > 0.01 : false

  async function salvar() {
    if (!form) return
    setSalvando(true)
    setMsgSalvar(null)
    try {
      if (form.tcv_cod) {
        await api.put(`/condicao-pagamento/${form.tcv_cod}`, form)
        setMsgSalvar({ texto: 'Alterações salvas com sucesso.', ok: true })
        await selecionar(form.tcv_cod)
      } else {
        const r = await api.post('/condicao-pagamento', form)
        setMsgSalvar({ texto: 'Condição de pagamento criada com sucesso.', ok: true })
        await selecionar(r.data.tcv_cod)
      }
      await carregarLista(busca)
    } catch (e: any) {
      setMsgSalvar({ texto: e?.response?.data?.error || 'Erro ao salvar.', ok: false })
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="cond-pagamento" style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
      {/* Lista */}
      <div className="card" style={{ width: 320, flexShrink: 0 }}>
        <h3 style={{
          fontSize: '1rem', fontWeight: 700, marginBottom: '0.9rem', color: '#1a6ea8',
          paddingBottom: '0.6rem', borderBottom: '2px solid #eff6ff',
        }}>
          Condição de Pagamento
        </h3>

        <div style={{ display: 'flex', gap: 8, marginBottom: '0.75rem', alignItems: 'center' }}>
          <div className="field" style={{ flex: 1 }}>
            <input
              type="text"
              placeholder="Código ou descrição..."
              value={busca}
              onChange={e => setBusca(e.target.value)}
            />
          </div>
          <button className="btn btn-primary btn-sm" onClick={nova} title="Nova condição de pagamento">+ Nova</button>
        </div>

        {carregandoLista && <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Carregando...</p>}
        {!carregandoLista && lista.length === 0 && (
          <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Nenhuma condição encontrada.</p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 560, overflowY: 'auto' }}>
          {lista.map(c => {
            const ativo = selecionado?.tcv_cod === c.tcv_cod
            return (
              <button
                key={c.tcv_cod}
                onClick={() => selecionar(c.tcv_cod)}
                style={{
                  textAlign: 'left', border: '1px solid #e5e7eb', borderLeft: '4px solid #1a6ea8',
                  borderRadius: 6, padding: '8px 10px', cursor: 'pointer',
                  background: ativo ? '#eff6ff' : '#f8fafc',
                  borderColor: ativo ? '#1a6ea8' : '#e5e7eb',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: '0.82rem' }}>{c.tcv_cod}</div>
                <div style={{ fontSize: '0.8rem', color: '#374151', marginTop: 3 }}>{c.tcv_des}</div>
                <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 3 }}>
                  {OPCOES_TIPO.find(o => o.value === c.tcv_tip)?.label ?? c.tcv_tip}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Detalhe */}
      <div className="card" style={{ flex: 1, minWidth: 0 }}>
        {!selecionado && !carregandoDetalhe && (
          <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
            Selecione uma condição de pagamento na lista ao lado, ou clique em "+ Nova".
          </p>
        )}

        {carregandoDetalhe && <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Carregando detalhes...</p>}

        {selecionado && form && !carregandoDetalhe && (
          <>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              marginBottom: '0.6rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff',
            }}>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a6ea8' }}>
                {selecionado.tcv_cod ? `${selecionado.tcv_cod} — ${selecionado.tcv_des || '(sem descrição)'}` : 'Nova Condição de Pagamento'}
              </h3>
            </div>

            <div style={{
              background: '#f7faff', border: '1px solid #eef0f4', borderLeft: '4px solid #1a6ea8',
              borderRadius: '0.5rem', padding: '0.55rem 0.85rem', marginBottom: '0.55rem',
              display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.8rem', alignItems: 'flex-end',
            }}>
              <Campo label="Código da Condição" width={95}>
                <input type="text" value={selecionado.tcv_cod || '(novo)'} readOnly disabled style={{ width: '100%' }} />
              </Campo>
              <Campo label="Descrição" width={320}>
                <input type="text" maxLength={30} value={form.tcv_des} onChange={e => setCampo('tcv_des', e.target.value)} style={{ width: '100%' }} />
              </Campo>
              <Campo label="Tipo / Comentário" width={160}>
                <select value={form.tcv_tip} onChange={e => setCampo('tcv_tip', e.target.value)} style={{ width: '100%' }}>
                  {OPCOES_TIPO.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Campo>
              <Campo label=" " width={200}>
                <input type="text" maxLength={10} value={form.tcv_com} onChange={e => setCampo('tcv_com', e.target.value)} style={{ width: '100%' }} />
              </Campo>
            </div>

            <div style={{
              background: '#fafafa', border: '1px solid #eef0f4', borderLeft: '4px solid #6b7280',
              borderRadius: '0.5rem', padding: '0.55rem 0.85rem', marginBottom: '0.55rem',
              display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.8rem', alignItems: 'flex-end',
            }}>
              <Campo label="Gera Liquidação Automaticamente" width={220}>
                <select value={form.tcv_gerliq} onChange={e => setCampo('tcv_gerliq', e.target.value)} style={{ width: '100%' }}>
                  <option value="S">Sim</option>
                  <option value="N">Não</option>
                </select>
              </Campo>
              <Campo label="Vencimento a Partir de" width={200}>
                <select value={form.tcv_fatcon} onChange={e => setCampo('tcv_fatcon', e.target.value)} style={{ width: '100%' }}>
                  {OPCOES_VENCIMENTO_A_PARTIR.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Campo>
              <Campo label="Pula Mês da Emissão" width={130}>
                <select value={form.tcv_pulemi} onChange={e => setCampo('tcv_pulemi', e.target.value)} style={{ width: '100%' }}>
                  <option value="N">Não</option>
                  <option value="S">Sim</option>
                </select>
              </Campo>
              <Campo label="Vencimento Sáb/Dom/Fer" width={150}>
                <select value={form.tcv_finsem} onChange={e => setCampo('tcv_finsem', e.target.value)} style={{ width: '100%' }}>
                  {OPCOES_VENCIMENTO_FDS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Campo>
              <Campo label="Observações" width={340}>
                <input type="text" maxLength={40} value={form.tcv_obs} onChange={e => setCampo('tcv_obs', e.target.value)} style={{ width: '100%' }} />
              </Campo>
            </div>

            <div style={{
              background: '#fff8f2', border: '1px solid #eef0f4', borderLeft: '4px solid #f07820',
              borderRadius: '0.5rem', padding: '0.55rem 0.85rem', marginBottom: '0.55rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: '0.5rem' }}>
                <h4 style={{
                  fontSize: '0.68rem', fontWeight: 700, color: '#f07820', textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                  Prazo das Parcelas em Dias
                </h4>
                <div style={{ display: 'flex', gap: 16, fontSize: '0.78rem', color: '#78716c' }}>
                  <span>Qtd. Vencimentos: <strong>{form.parcelas.length}</strong></span>
                  <span>
                    Total das Parcelas:{' '}
                    <strong style={{ color: totalDivergente ? '#dc2626' : '#15803d' }}>{fmtPercentual(totalParcelas)}%</strong>
                  </span>
                </div>
              </div>

              <table style={{ width: 'auto', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #fed7aa' }}>
                    <th style={{ width: 50, textAlign: 'left', padding: '4px 6px' }}>Num</th>
                    <th style={{ width: 110, textAlign: 'left', padding: '4px 6px' }}>Dias</th>
                    <th style={{ width: 120, textAlign: 'left', padding: '4px 6px' }}>Percentual</th>
                    <th style={{ width: 40 }} />
                  </tr>
                </thead>
                <tbody>
                  {form.parcelas.map((p, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '4px 6px' }}>{p.num}</td>
                      <td style={{ padding: '4px 6px' }}>
                        <input
                          type="number" value={p.dias}
                          onChange={e => setParcela(idx, 'dias', Number(e.target.value))}
                          style={{ width: 80 }}
                        />
                      </td>
                      <td style={{ padding: '4px 6px' }}>
                        <input
                          type="number" step="0.01" value={p.percentual}
                          onChange={e => setParcela(idx, 'percentual', Number(e.target.value))}
                          style={{ width: 100 }}
                        />
                      </td>
                      <td style={{ padding: '4px 6px' }}>
                        <button
                          onClick={() => removerParcela(idx)}
                          disabled={form.parcelas.length <= 1}
                          title="Remover parcela"
                          style={{
                            border: 'none', background: 'none', cursor: form.parcelas.length > 1 ? 'pointer' : 'not-allowed',
                            color: form.parcelas.length > 1 ? '#dc2626' : '#d1d5db', fontWeight: 700,
                            fontSize: '1.15rem', lineHeight: 1, padding: '2px 6px',
                          }}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <button className="btn" onClick={adicionarParcela} style={{ marginTop: '0.6rem' }}>
                + Adicionar parcela
              </button>
              {totalDivergente && (
                <p style={{ color: '#dc2626', fontSize: '0.78rem', marginTop: '0.5rem' }}>
                  A soma dos percentuais deveria totalizar 100%.
                </p>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button className="btn btn-primary" onClick={salvar} disabled={salvando || totalDivergente}>
                {salvando ? 'Salvando...' : 'Salvar alterações'}
              </button>
              {msgSalvar && (
                <span style={{
                  fontSize: '0.8rem', fontWeight: 600, padding: '0.35rem 0.8rem', borderRadius: 6,
                  background: msgSalvar.ok ? '#d1fae5' : '#fee2e2',
                  color: msgSalvar.ok ? '#065f46' : '#991b1b',
                }}>
                  {msgSalvar.texto}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
